import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Users, UserCheck, UserX, TrendingUp, Search, ChevronUp, ChevronDown, CalendarDays } from 'lucide-react';
import Header from '../../../components/header/Header';
import Footer from '../../../components/footer/Footer';
import HeroCarousel from '../../../components/carousel/HeroCarousel';
import Breadcrumb from '../../../shared/components/Breadcrumb';
import img1 from '../../../assets/attendenceCarousel.png';
import { attendanceApi } from '../services/api';
import KVSLoader from '../../../components/loaders/KVSLoader';

interface RegionData {
  roID: number;
  roName: string;
  totalStudents: number;
  totalEnrollment: number;
  totalPresent: number;
  totalAbsent: number;
  notMarked: number;
  totalSection: number;
  totalPendingSection: number;
  totalSectionCount: number;
  sectionsWithPresent: number;
  attendancePercentage: number;
  dataConsistencyPercentage: number;
  status: number;
}

interface SummaryData {
  totalStudents: number;
  totalEnrollment: number;
  totalPresent: number;
  totalAbsent: number;
  notMarked: number;
  totalSection: number;
  totalPendingSection: number;
  totalSectionCount: number;
  sectionsWithPresent: number;
  overallAttendancePercentage: number;
  dataConsistencyPercentage: number;
  status: number;
}

type SortField = 'roName' | 'totalStudents' | 'totalEnrollment' | 'notMarked' | 'totalPresent' | 'totalAbsent' | 'attendancePercentage' | 'totalSection' | 'totalPendingSection' | 'totalSectionCount' | 'sectionsWithPresent' | 'status';
type SortOrder = 'asc' | 'desc';

const getTodayLabel = () => {
  return new Date().toLocaleDateString('en-IN', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const getAttendanceColor = (pct: number) => {
  if (pct >= 90) return { bar: 'bg-green-500', text: 'text-green-700', badge: 'bg-green-100 text-green-700' };
  if (pct >= 75) return { bar: 'bg-blue-500', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700' };
  if (pct >= 60) return { bar: 'bg-yellow-500', text: 'text-yellow-700', badge: 'bg-yellow-100 text-yellow-700' };
  return { bar: 'bg-red-500', text: 'text-red-700', badge: 'bg-red-100 text-red-700' };
};

const getPendingSectionValue = (item: { totalPendingSection?: number; totalPending?: number; totalSection?: number }) => {
  if (typeof item.totalPendingSection === 'number') return item.totalPendingSection;
  if (typeof item.totalPending === 'number') return item.totalPending;
  if (typeof item.totalSection === 'number') return item.totalSection;
  return 0;
};

const getTotalSectionValue = (item: { totalSection?: number; totalSectionCount?: number }) => {
  if (typeof item.totalSection === 'number') return item.totalSection;
  if (typeof item.totalSectionCount === 'number') return item.totalSectionCount;
  return 0;
};

const getTotalSectionCountValue = (
  item: { totalSectionCount?: number; sectionsWithPresent?: number; totalPendingSection?: number; totalPending?: number },
) => {
  if (typeof item.totalSectionCount === 'number') return item.totalSectionCount;
  const sectionsWithPresent = typeof item.sectionsWithPresent === 'number' ? item.sectionsWithPresent : 0;
  const totalPendingSection = getPendingSectionValue(item);
  return sectionsWithPresent + totalPendingSection;
};

// Backend may omit numeric fields entirely for some rows/dates; default to 0
// so `.toLocaleString()` / `.toFixed()` never blow up on undefined.
const num = (value: unknown): number => (typeof value === 'number' && !isNaN(value) ? value : 0);

export default function TodaysAttendanceOverview() {
  const navigate = useNavigate();
  const carouselImages = [img1];
  const [regions, setRegions] = useState<RegionData[]>([]);
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('attendancePercentage');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const today = new Date();
  const [selectedDate, setSelectedDate] = useState<string>(today.toISOString().slice(0, 10));
  const [calendarOpen, setCalendarOpen] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);

  // Close calendar on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (calendarRef.current && !calendarRef.current.contains(e.target as Node)) {
        setCalendarOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Build calendar grid for the current month of selectedDate
  const calendarDays = useMemo(() => {
    const base = new Date(selectedDate + 'T00:00:00');
    const year = base.getFullYear();
    const month = base.getMonth();
    const firstDay = new Date(year, month, 1).getDay(); // 0=Sun
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const todayStr = today.toISOString().slice(0, 10);
    const cells: { date: string; day: number; disabled: boolean; isToday: boolean }[] = [];
    for (let i = 0; i < firstDay; i++) cells.push({ date: '', day: 0, disabled: true, isToday: false });
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      cells.push({ date: dateStr, day: d, disabled: dateStr > todayStr, isToday: dateStr === todayStr });
    }
    return { cells, year, month };
  }, [selectedDate]);

  const monthLabel = useMemo(() => {
    const base = new Date(selectedDate + 'T00:00:00');
    return base.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
  }, [selectedDate]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const todayStr = today.toISOString().slice(0, 10);
        const response = await attendanceApi.getTodaysDetailedAttendance(
          selectedDate !== todayStr ? selectedDate : undefined,
        );
        if (response?.success) {
          const normalizedRegions = (response.data || []).map((region: RegionData & { totalPending?: number }) => ({
            ...region,
            totalStudents: num(region.totalStudents),
            totalEnrollment: num(region.totalEnrollment),
            totalPresent: num(region.totalPresent),
            totalAbsent: num(region.totalAbsent),
            notMarked: num(region.notMarked),
            attendancePercentage: num(region.attendancePercentage),
            dataConsistencyPercentage: num(region.dataConsistencyPercentage),
            status: num(region.status),
            sectionsWithPresent: num(region.sectionsWithPresent),
            roName: region.roName || 'Unknown',
            totalSection: getTotalSectionValue(region),
            totalPendingSection: getPendingSectionValue(region),
            totalSectionCount: getTotalSectionCountValue(region),
          }));

          const normalizedSummary = response.summary
            ? {
                ...response.summary,
                totalStudents: num(response.summary.totalStudents),
                totalEnrollment: num(response.summary.totalEnrollment),
                totalPresent: num(response.summary.totalPresent),
                totalAbsent: num(response.summary.totalAbsent),
                notMarked: num(response.summary.notMarked),
                overallAttendancePercentage: num(response.summary.overallAttendancePercentage),
                dataConsistencyPercentage: num(response.summary.dataConsistencyPercentage),
                status: num(response.summary.status),
                sectionsWithPresent: num(response.summary.sectionsWithPresent),
                totalSection: getTotalSectionValue(response.summary as SummaryData),
                totalPendingSection: getPendingSectionValue(response.summary as SummaryData & { totalPending?: number }),
                totalSectionCount: getTotalSectionCountValue(response.summary as SummaryData & { totalPending?: number }),
              }
            : null;

          setRegions(normalizedRegions);
          setSummary(normalizedSummary);
        }
      } catch {
        // silently fail
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [selectedDate]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder(field === 'roName' ? 'asc' : 'desc');
    }
  };

  const filteredAndSorted = useMemo(() => {
    let list = regions.filter(r =>
      r.roName.toLowerCase().includes(searchQuery.toLowerCase())
    );
    list = [...list].sort((a, b) => {
      const dir = sortOrder === 'asc' ? 1 : -1;
      if (sortField === 'roName') {
        return a.roName.localeCompare(b.roName) * dir;
      }
      return (a[sortField] - b[sortField]) * dir;
    });
    return list;
  }, [regions, searchQuery, sortField, sortOrder]);

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) return <ChevronUp className="w-3 h-3 opacity-30" />;
    return sortOrder === 'asc'
      ? <ChevronUp className="w-3 h-3 text-purple-300" />
      : <ChevronDown className="w-3 h-3 text-purple-300" />;
  };

  const statCards = summary
    ? [
        {
          label: 'Total Students',
          value: summary.totalStudents.toLocaleString(),
          icon: <Users className="w-6 h-6 text-white" />,
          bg: 'bg-[#57C3F2]',
          ring: 'ring-[#57C3F2]/30',
        },
        {
          label: 'Total Present',
          value: summary.totalPresent.toLocaleString(),
          icon: <UserCheck className="w-6 h-6 text-white" />,
          bg: 'bg-[#279E15]',
          ring: 'ring-[#279E15]/30',
        },
        {
          label: 'Total Absent',
          value: summary.totalAbsent.toLocaleString(),
          icon: <UserX className="w-6 h-6 text-white" />,
          bg: 'bg-[#FF0000]',
          ring: 'ring-[#FF0000]/30',
        },
        {
          label: 'Overall Attendance',
          value: `${summary.overallAttendancePercentage.toFixed(2)}%`,
          icon: <TrendingUp className="w-6 h-6 text-white" />,
          bg: 'bg-[#E4B124]',
          ring: 'ring-[#E4B124]/30',
        },
      ]
    : [];

  const sectionCards = summary
    ? [
        { label: 'Total Attendance', value: summary.totalEnrollment.toLocaleString(), sub: 'Students for whom attendance was taken', color: 'text-indigo-700', bg: 'bg-indigo-50', border: 'border-indigo-200' },
        { label: 'Not Marked', value: summary.notMarked.toLocaleString(), sub: 'Students with no attendance marked', color: 'text-orange-700', bg: 'bg-orange-50', border: 'border-orange-200' },
        { label: 'Active Sections (RO)', value: summary.totalSection.toLocaleString(), sub: 'Total active sections across all ROs', color: 'text-blue-700', bg: 'bg-blue-50', border: 'border-blue-200' },
        { label: 'Total Section Count', value: summary.totalSectionCount.toLocaleString(), sub: 'Total sections in scope today', color: 'text-purple-700', bg: 'bg-purple-50', border: 'border-purple-200' },
        { label: 'Sections Marked', value: summary.sectionsWithPresent.toLocaleString(), sub: 'Sections where attendance is marked', color: 'text-indigo-700', bg: 'bg-indigo-50', border: 'border-indigo-200' },
        { label: 'Pending Sections', value: summary.totalPendingSection.toLocaleString(), sub: 'Sections pending attendance marking', color: 'text-green-700', bg: 'bg-green-50', border: 'border-green-200' },
        { label: 'Status', value: `${summary.status}%`, sub: 'Attendance submission status', color: summary.status >= 90 ? 'text-green-700' : summary.status >= 60 ? 'text-yellow-700' : 'text-red-700', bg: summary.status >= 90 ? 'bg-green-50' : summary.status >= 60 ? 'bg-yellow-50' : 'bg-red-50', border: summary.status >= 90 ? 'border-green-200' : summary.status >= 60 ? 'border-yellow-200' : 'border-red-200' },
      ]
    : [];

  return (
    <div className="min-h-screen flex flex-col gap-0 bg-[#f5f5f7]">
      <Header />
      <HeroCarousel images={carouselImages} autoRotateInterval={3000} />
      <div className="w-full max-w-7xl px-4 pt-0 sm:pt-0">
        <Breadcrumb />
      </div>
      <main className="flex-grow w-full max-w-8xl mx-auto px-4 py-5 md:px-8 lg:px-16">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/attendance?role=student')}
              className="p-2 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 shadow-sm transition"
              aria-label="Back"
            >
              <ArrowLeft className="w-4 h-4 text-gray-600" />
            </button>
            <div>
              <h2 className="text-2xl font-bold text-[#6A1786]">Today's Attendance Overview</h2>
              <p className="text-sm text-gray-500">{getTodayLabel()}</p>
            </div>
          </div>

          {/* Date picker */}
          <div className="relative" ref={calendarRef}>
            <button
              onClick={() => setCalendarOpen(o => !o)}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-200 bg-white shadow-sm hover:bg-gray-50 transition text-sm font-medium text-gray-700"
            >
              <CalendarDays className="w-4 h-4 text-[#6A1786]" />
              {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
            </button>

            {calendarOpen && (
              <div className="absolute right-0 mt-2 z-50 bg-white border border-gray-200 rounded-xl shadow-xl p-4 w-72">
                <p className="text-sm font-semibold text-center text-[#6A1786] mb-3">{monthLabel}</p>
                <div className="grid grid-cols-7 gap-0.5 mb-1">
                  {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
                    <div key={d} className="text-center text-[10px] font-semibold text-gray-400 py-1">{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-0.5">
                  {calendarDays.cells.map((cell, i) => (
                    <button
                      key={i}
                      disabled={cell.disabled || !cell.date}
                      onClick={() => {
                        if (cell.date) {
                          setSelectedDate(cell.date);
                          setCalendarOpen(false);
                        }
                      }}
                      className={[
                        'w-8 h-8 mx-auto rounded-full text-xs font-medium transition flex items-center justify-center',
                        !cell.date ? 'invisible' : '',
                        cell.disabled ? 'text-gray-300 cursor-not-allowed' : 'hover:bg-purple-100 cursor-pointer',
                        cell.date === selectedDate ? 'bg-[#6A1786] text-white hover:bg-[#6A1786]' : '',
                        cell.isToday && cell.date !== selectedDate ? 'ring-1 ring-[#6A1786] text-[#6A1786]' : '',
                      ].join(' ')}
                    >
                      {cell.day || ''}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-32 gap-4">
            <KVSLoader size={80} />
            <span className="text-sm text-gray-400">Loading today's attendance data...</span>
          </div>
        ) : (
          <>
          {/* Summary Stat Cards */}
          {summary && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-4 gap-4 mb-4">
                {statCards.map((card, i) => (
                  <div
                    key={i}
                    className={`bg-white rounded-xl shadow border border-gray-100 p-5 flex items-center gap-4 ring-2 ${card.ring}`}
                  >
                    <div className={`flex-shrink-0 w-12 h-12 rounded-xl ${card.bg} flex items-center justify-center`}>
                      {card.icon}
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{card.label}</p>
                      <p className="text-2xl font-bold text-[#6A1786] mt-0.5">{card.value}</p>
                    </div>
                  </div>
                ))}
              </div>
              {/* Section Info Cards */}
              <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-7 gap-3 mb-8">
                {sectionCards.map((card, i) => (
                  <div key={i} className={`rounded-lg border ${card.border} ${card.bg} px-4 py-3`}>
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-0.5">{card.label}</p>
                    <p className={`text-lg font-bold ${card.color}`}>{card.value}</p>
                    <p className="text-[10px] text-gray-400 mt-0.5 leading-tight">{card.sub}</p>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Search & count bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h3 className="text-base font-semibold text-gray-700">
              Region-wise Breakdown
              <span className="ml-2 text-sm font-normal text-gray-400">({filteredAndSorted.length} regions)</span>
            </h3>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search region..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg bg-white shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-300 w-52"
              />
            </div>
          </div>

          {/* Region Table */}
          <div className="bg-white rounded-xl shadow overflow-hidden border border-gray-100">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="bg-gradient-to-r from-[#481789] to-[#6A1786] text-white">
                    <th className="px-4 py-3 text-left font-semibold w-10">#</th>
                    <th
                      className="px-4 py-3 text-left font-semibold cursor-pointer select-none hover:bg-white/10 transition"
                      onClick={() => handleSort('roName')}
                    >
                      <div className="flex items-center gap-1">
                        Region <SortIcon field="roName" />
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-right font-semibold cursor-pointer select-none hover:bg-white/10 transition"
                      onClick={() => handleSort('totalStudents')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Total Students <SortIcon field="totalStudents" />
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-right font-semibold cursor-pointer select-none hover:bg-white/10 transition"
                      onClick={() => handleSort('totalEnrollment')}
                    >
                      <div className="flex items-center justify-end gap-1">
                       Total Attendance <SortIcon field="totalEnrollment" />
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-right font-semibold cursor-pointer select-none hover:bg-white/10 transition"
                      onClick={() => handleSort('totalPresent')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Present <SortIcon field="totalPresent" />
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-right font-semibold cursor-pointer select-none hover:bg-white/10 transition"
                      onClick={() => handleSort('totalAbsent')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Absent <SortIcon field="totalAbsent" />
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-right font-semibold cursor-pointer select-none hover:bg-white/10 transition"
                      onClick={() => handleSort('notMarked')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Not Marked <SortIcon field="notMarked" />
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-right font-semibold cursor-pointer select-none hover:bg-white/10 transition min-w-[160px]"
                      onClick={() => handleSort('attendancePercentage')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Attendance % <SortIcon field="attendancePercentage" />
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-right font-semibold cursor-pointer select-none hover:bg-white/10 transition"
                      onClick={() => handleSort('totalSection')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Active Sections <SortIcon field="totalSection" />
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-right font-semibold cursor-pointer select-none hover:bg-white/10 transition"
                      onClick={() => handleSort('totalSectionCount')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Total Sections <SortIcon field="totalSectionCount" />
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-right font-semibold cursor-pointer select-none hover:bg-white/10 transition"
                      onClick={() => handleSort('totalPendingSection')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Pending Sections <SortIcon field="totalPendingSection" />
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-right font-semibold cursor-pointer select-none hover:bg-white/10 transition"
                      onClick={() => handleSort('sectionsWithPresent')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Sections Marked <SortIcon field="sectionsWithPresent" />
                      </div>
                    </th>
                    <th
                      className="px-4 py-3 text-right font-semibold cursor-pointer select-none hover:bg-white/10 transition"
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Status <SortIcon field="status" />
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAndSorted.length === 0 ? (
                    <tr>
                      <td colSpan={13} className="px-4 py-16 text-center text-gray-400">
                        No regions found
                      </td>
                    </tr>
                  ) : (
                    filteredAndSorted.map((region, idx) => {
                      const colors = getAttendanceColor(region.attendancePercentage);
                      return (
                        <tr
                          key={region.roID}
                          className={`border-b border-gray-100 hover:bg-purple-50/40 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}
                        >
                          <td className="px-4 py-3 text-gray-400 font-medium">{idx + 1}</td>
                          <td className="px-4 py-3 font-semibold text-gray-800">{region.roName}</td>
                          <td className="px-4 py-3 text-right text-gray-700 font-medium">
                            {region.totalStudents.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-700 font-medium">
                            {region.totalEnrollment.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-green-700">
                            {region.totalPresent.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-red-600">
                            {region.totalAbsent.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right font-semibold text-orange-600">
                            {region.notMarked.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex flex-col items-end gap-1">
                              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${colors.badge}`}>
                                {region.attendancePercentage.toFixed(2)}%
                              </span>
                              <div className="w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                                <div
                                  className={`h-full rounded-full ${colors.bar}`}
                                  style={{ width: `${Math.min(region.attendancePercentage, 100)}%` }}
                                />
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right text-gray-700 font-medium">
                            {region.totalSection.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-700 font-medium">
                            {region.totalSectionCount.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right text-gray-700 font-medium">
                            {region.totalPendingSection.toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="text-xs font-semibold text-indigo-700">
                              {region.sectionsWithPresent.toLocaleString()}
                            </span>
                            <span className="text-xs text-gray-400"> / {region.totalSectionCount.toLocaleString()}</span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                              region.status >= 90 ? 'bg-green-100 text-green-700'
                              : region.status >= 60 ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-red-100 text-red-700'
                            }`}>
                              {region.status}%
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
                {filteredAndSorted.length > 0 && summary && (
                  <tfoot>
                    <tr className="bg-gradient-to-r from-[#481789]/10 to-[#6A1786]/10 border-t-2 border-purple-200">
                      <td className="px-4 py-3" />
                      <td className="px-4 py-3 font-bold text-[#6A1786]">All Regions Total</td>
                      <td className="px-4 py-3 text-right font-bold text-gray-800">
                        {summary.totalStudents.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-gray-800">
                        {summary.totalEnrollment.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-green-700">
                        {summary.totalPresent.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-red-600">
                        {summary.totalAbsent.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-orange-600">
                        {summary.notMarked.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${getAttendanceColor(summary.overallAttendancePercentage).badge}`}>
                          {summary.overallAttendancePercentage.toFixed(2)}%
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-gray-800">
                        {summary.totalSection.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-gray-800">
                        {summary.totalSectionCount.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right font-bold text-gray-800">
                        {summary.totalPendingSection.toLocaleString()}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className="text-xs font-bold text-indigo-700">
                          {summary.sectionsWithPresent.toLocaleString()}
                        </span>
                        <span className="text-xs text-gray-400"> / {summary.totalSectionCount.toLocaleString()}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                          summary.status >= 90 ? 'bg-green-100 text-green-700'
                          : summary.status >= 60 ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-red-100 text-red-700'
                        }`}>
                          {summary.status}%
                        </span>
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </div>
        </>
      )}
      </main>
      <Footer />
    </div>
  );
}
